import React, { FC, Fragment, useState } from "react";
import { Combobox, Switch, Transition } from "@headlessui/react";
import { TimeZone } from "../../../utils/types";
import { HiSelector, HiCheck } from "react-icons/hi";
import * as Sentry from "@sentry/nextjs";

type Props = {
  options: TimeZone[];
  selectedOption: TimeZone;
  setSelectedOption: React.Dispatch<React.SetStateAction<TimeZone>>;
};

const TZComboBox: FC<Props> = ({
  options,
  selectedOption,
  setSelectedOption,
}) => {
  //timeZone values
  const [query, setQuery] = useState("");
  //Switch to see current times in 24hr
  const [is24Hour, setIs24Hour] = useState(false);
  //Filter timezones alphabetically
  const filteredOptions = !Array.isArray(options)
    ? []
    : query === ""
    ? options
    : options.filter((option) => {
        return option.bclId.toLowerCase().includes(query.toLowerCase());
      });

  //Format timeZone for display
  const displayTimeZoneTime = (tzId: string) => {
    try {
      return new Date()
        .toLocaleTimeString(navigator.language, {
          timeZone: tzId,
          hour: "2-digit",
          minute: "2-digit",
          hour12: !is24Hour,
        })
        .replace(/^0+/, "");
    } catch (error) {
      console.warn("Failed to format timezone time:", error);
      Sentry.captureException(error);
    }
    return "";
  };

  return (
    <div className="w-full">
      <Combobox value={selectedOption} onChange={setSelectedOption}>
        {({ open }) => (
          <div className="relative mt-1">
            <Combobox.Button
              as="div"
              className="relative flex w-full items-center justify-between overflow-hidden text-left bg-white rounded-lg shadow-md cursor-pointer focus-within:ring-2 focus-within:ring-white focus-within:ring-opacity-75 focus-within:ring-offset-2 focus-within:ring-offset-teal-300 sm:text-sm"
            >
              <Combobox.Input
                className="hidden w-full py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 border-none focus:ring-0 focus:outline-none lg:block cursor-pointer"
                displayValue={(option: TimeZone) => option?.bclId}
                onChange={(event) => setQuery(event.target.value)}
              />
              
              {/* Intercept clicks when closed so the entire row opens the dropdown */}
              {!open && (
                <div className="absolute inset-0 z-10 w-full h-full" />
              )}

              <div className="block m-2 lg:hidden pointer-events-none">
                <Combobox.Label>TimeZone: {selectedOption?.bclId}</Combobox.Label>
              </div>
              
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <HiSelector
                  className="w-5 h-5 text-gray-400"
                  aria-hidden="true"
                />
              </div>
            </Combobox.Button>

            <Transition
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
              afterLeave={() => setQuery("")}
            >
              <Combobox.Options className="absolute z-10 h-64 w-full min-w-full py-1 mt-1 overflow-scroll text-base bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                <div className="flex flex-row justify-between px-3">
                  <span>Display in 24 hr</span>
                  <span>
                    <Switch
                      checked={is24Hour}
                      onChange={setIs24Hour}
                      className={`${
                        is24Hour ? "bg-confirmed-secondary" : "bg-gray-200"
                      } relative inline-flex h-6 w-10 items-center rounded-full focus:outline-none`}
                    >
                      <span className="sr-only">Enable notifications</span>
                      <span
                        className={`${
                          is24Hour ? "translate-x-5" : "translate-x-1"
                        } inline-block h-4 w-4 transform rounded-full bg-white`}
                      />
                    </Switch>
                  </span>
                </div>
                {filteredOptions.length === 0 && query !== "" ? (
                  <div className="relative px-4 py-2 text-gray-700 cursor-default select-none">
                    Nothing found.
                  </div>
                ) : (
                  filteredOptions.map((option, idx) => (
                    <Combobox.Option
                      key={idx}
                      className={({ active }) =>
                        `relative cursor-default select-none py-2 px-3 ${
                          active
                            ? "bg-confirmed-secondary text-white"
                            : "text-gray-900"
                        }`
                      }
                      value={option}
                    >
                      {({ selected, active }) => (
                        <div className="flex flex-row justify-between whitespace-no-wrap">
                          <span
                            className={`block truncate ${
                              selected ? "font-medium" : "font-normal"
                            }`}
                          >
                            {option.bclId}
                          </span>
                          <span>{displayTimeZoneTime(option.tzdbId)}</span>
                          {selected ? (
                            <span
                              className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                active ? "text-white" : "text-confirmed-secondary"
                              }`}
                            />
                          ) : null}
                        </div>
                      )}
                    </Combobox.Option>
                  ))
                )}
              </Combobox.Options>
            </Transition>
          </div>
        )}
      </Combobox>
    </div>
  );
};

export default TZComboBox;
